// WF-CONTROL: Setup › Claude Code connection's queries (the Workforce Control add-on, the caller's
// connections) and the profile revisions' runtime state a DE or agent page reads. Every key sits
// under `["company", co, …]`, so the company's change pings refresh it.
import type { OperationOutput } from "@companyos/sdk";
import { queryOptions } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import { keys } from "../queries";

export type PluginState = OperationOutput<"workforcePlugin.get">;
export type ControlToken = OperationOutput<"control.tokens.list">["data"][number];
export type ControlTokenCreated = OperationOutput<"control.tokens.create">;

export const controlKeys = {
  plugin: (co: string) => ["company", co, "workforce-plugin"] as const,
  tokens: (co: string) => ["company", co, "control-tokens"] as const,
};

export const workforcePluginQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: controlKeys.plugin(co),
    queryFn: () => api.client.workforcePlugin.get({ params: { co } }),
  });

export const controlTokensQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: controlKeys.tokens(co),
    queryFn: () => api.client.control.tokens.list({ params: { co } }),
  });

/** The newest revision only: the page's `runtime` is what the chip needs. Under the DE's key. */
export const deRevisionsQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: [...keys.de(co, id), "profile-revisions"] as const,
    queryFn: () =>
      api.client.digitalEmployees.profileRevisions({ params: { co, id }, query: { limit: 1 } }),
  });

/** The same for an agent, under the agents list's prefix (an edit refreshes it). */
export const agRevisionsQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: [...keys.workforceAgents(co), "one", id, "profile-revisions"] as const,
    queryFn: () =>
      api.client.workforceAgents.profileRevisions({ params: { co, id }, query: { limit: 1 } }),
  });

/** The one command that pairs and registers the MCP server (the code is used once). */
export function pairCommand(apiOrigin: string, code: string): string {
  const body = JSON.stringify({ code });
  return `claude mcp add --transport http companyos ${apiOrigin}/mcp/control --header "Authorization: Bearer $(curl -fsS -X POST ${apiOrigin}/v1/control/pair -H 'Accept: text/plain' -H 'Content-Type: application/json' -d '${body}')"`;
}
